// umlaut-check: skip — technische Schlüssel der Bestandskennzahlen (JSON-Schnittstelle).
import React from 'react';
import OeTip from './OeTip';
import BestandTooltip, {werteAusKennzahlen} from '../Stock/BestandTooltip';
import {fmt0, qtyNum} from './format';
import {STAMM_LABELS, STAMM_NONE, WH_OF_STAMM, freeAtWh} from './availability';

/**
 * Schieberegler „Liefern ab" je Position (Original `lagerSlider`, Zeile 951).
 *
 * Jedes Segment steht für ein Lager und zeigt darunter den verfügbaren Bestand
 * des Artikels genau in diesem Lager (Lagerbestand − von anderen Aufträgen reserviert) — grün, wenn er den offenen Bedarf deckt, rot
 * bei nichts mehr da, sonst amber. Ein Klick setzt die Positions-Zuordnung;
 * wirksam wird sie mit dem Speichern.
 *
 * Drittes Segment „Keine" (PLAN.md 3.10, ABGLEICH H27): Die Position wird gar
 * nicht geliefert. Sie hat damit kein Lager, keinen Bestandswert und keine
 * Farbe — und landet später in der Fehlmengen-Datei.
 *
 * @param {object} row
 * @param {number} index - Platz in der Arbeitsliste (nicht in der Ansicht).
 * @param {string} current - aufgelöste Wahl (`stammLagerOf`).
 * @param {object} stock
 * @param {boolean} editable - ohne Bearbeitungsrecht bzw. am gesperrten Beleg gesperrt.
 * @param {(index: number, value: string) => void} onChange
 * @param {string} [orderRef] - Beleg-Referenz für die Liste der reservierenden Belege im Tooltip.
 */
export default function WarehouseSlider({row, index, current, stock, editable, onChange, orderRef}) {
    const need = qtyNum(row.quantity) - qtyNum(row.shippedQuantity);
    const inPflege = stock.lahrStockTrusted === false;

    // Cosmos ist ein vollwertiges Lager (R2): Es erscheint immer, mit eigener
    // Bestandszahl, eigenem Lieferschein und automatischer Reservierung.
    const segments = ['Lahr', 'SNV', 'Cosmos', STAMM_NONE];

    return (
        <div className="slider" role="group" aria-label="Lieferlager dieser Position">
            {segments.map((o) => {
                const label = STAMM_LABELS[o];
                const active = o === current;

                if (o === STAMM_NONE) {
                    return (
                        <OeTip
                            key={o}
                            tip={'Diese Position gar nicht liefern. Sie zählt dann weder in den Bestand noch in '
                                + 'Gewicht und Rabattstaffel und erscheint später in der Fehlmengen-Datei. '
                                + 'Wirksam mit „Speichern".'}
                        >
                            <button
                                type="button"
                                className={`slseg noneseg${active ? ' on' : ''}`}
                                aria-pressed={active}
                                disabled={!editable}
                                onClick={() => onChange(index, o)}
                            >
                                {label}
                            </button>
                        </OeTip>
                    );
                }

                const warehouseId = WH_OF_STAMM[o];
                const f = freeAtWh(row, warehouseId, stock);
                const cls = f === undefined ? '' : (f >= need && need > 0 ? 'okf' : (f <= 0 ? 'nof' : 'lowf'));
                const fstr = f === undefined ? '–' : fmt0(f);
                const pflege = inPflege && o === 'Lahr';
                const figures = ((stock.avail || {})[String(row.articleId)] || {})[warehouseId];
                const werte = figures ? werteAusKennzahlen(figures) : {verfuegbar: f};

                return (
                    <BestandTooltip
                        key={o}
                        werte={werte}
                        lagerName={STAMM_LABELS[o] === 'Schneider' ? 'SNV Weilheim (Schneider)' : label}
                        kopf={`Ab ${label} liefern · verfügbar: ${fstr}`}
                        quelle={(pflege ? 'Der Lahr-Bestand wird in weclapp noch aufgebaut — die Zahl kann ein Platzhalter sein. ' : '')
                            + (editable ? 'Wirksam mit „Speichern".' : '')}
                        haelterUrl={orderRef && row.articleId
                            ? `/order-editor/${encodeURIComponent(orderRef)}/reservations/${encodeURIComponent(row.articleId)}`
                            : undefined}
                        haelterParams={{warehouse: warehouseId}}
                    >
                        <button
                            type="button"
                            className={`slseg ${cls}${active ? ' on' : ''}`}
                            aria-pressed={active}
                            disabled={!editable}
                            onClick={() => onChange(index, o)}
                        >
                            {label}{pflege ? <span className="segpflege" aria-label="Bestand in Pflege"> ⚠</span> : null}
                            <span className="segfree">{fstr}</span>
                        </button>
                    </BestandTooltip>
                );
            })}
        </div>
    );
}
